import { Box, Button, Container, Header, SpaceBetween, StatusIndicator, Table } from '@cloudscape-design/components';
import { FC, JSX } from 'react';
import { useModuleTranslation } from '../../lang';
import { ProductRow } from '../../pages/AgpOverview.logic';
import { AgpTableBaseProps } from './types';

type Props = AgpTableBaseProps & {
  productRows: ProductRow[];
  onReleaseClick?: () => void;
  onVoidClick?: () => void;
};

const StockByProductTable: FC<Props> = ({
  productRows,
  onReleaseClick,
  onVoidClick,
  headerActions,
  refresh,
  loading,
  loadingText,
  emptyText,
  testId,
}) => {
  const t = useModuleTranslation('Agp.StockByProductTable');
  return (
    <Container data-testid={testId}>
      <Header
        variant="h2"
        actions={
          <SpaceBetween size="xs" direction="horizontal">
            {onReleaseClick && (
              <Button key="release" onClick={onReleaseClick} disabled={loading}>
                {t.release}
              </Button>
            )}
            {onVoidClick && (
              <Button key="void" onClick={onVoidClick} disabled={loading}>
                Void
              </Button>
            )}
            {refresh && <Button key="refresh" iconName="refresh" variant="icon" onClick={refresh} disabled={loading} />}
            {headerActions}
          </SpaceBetween>
        }
      >
        {t.header}
      </Header>

      <Table<ProductRow>
        trackBy={(r) => `${r.slug}::${r.bottleName}`}
        variant="borderless"
        stickyHeader
        loading={loading}
        loadingText={loadingText}
        columnDefinitions={[
          { header: t.product, cell: (r) => r.slug.charAt(0).toUpperCase() + r.slug.slice(1) },
          { header: t.bottleSize, cell: (r) => r.bottleName },
          {
            header: t.inAgp,
            cell: (r) => <span style={{ fontWeight: 600, color: r.onHand < 0 ? 'red' : undefined }}>{r.onHand}</span>,
          },
          { header: t.bottled, cell: (r) => r.inTotal },
          { header: t.released, cell: (r) => r.outTotal },
          {
            header: 'Voided',
            cell: (r) => r.voidedTotal,
          },
          {
            header: t.batches,
            cell: (r) => {
              if (!r.batches.length) return '—';

              const nodes = r.batches.map((b) =>
                b.depleted ? (
                  <StatusIndicator key={b.batchId} type="error">
                    {b.batchShort} ({t.emptyBatch})
                  </StatusIndicator>
                ) : (
                  <span key={b.batchId}>
                    {b.batchShort} ({b.onHand})
                  </span>
                ),
              );

              // Render as comma-separated inline items
              return nodes.reduce<JSX.Element[]>((acc, el, idx) => {
                if (idx > 0) acc.push(<span key={`sep-${idx}`}>, </span>);
                acc.push(el);
                return acc;
              }, []);
            },
          },
        ]}
        items={productRows}
        empty={<Box padding="m">{emptyText ?? t.empty}</Box>}
      />
    </Container>
  );
};

export default StockByProductTable;
